import { get, post } from "../http-client/client";
import { ApiResponse } from "../http-client/Response";

// Stages match the V2 contract backend
export enum ContractStage {
  INIT = 0,
  INFO = 1,
  SECTIONS = 2,
  REGENERATE = 3,
  COMPLETED = 4,
}

export enum ContractJobStatus {
  IDLE = "idle",
  PROCESSING = "processing",
  COMPLETED = "completed",
  FAILED = "failed",
  CANCELLED = "cancelled",
}

export type PartyFieldType = "short_text" | "long_text" | "date";

export interface ContractPartyField {
  type: PartyFieldType;
  label: string;
  promptHint: string;
  userResponse?: string;
  pre_fill?: string;
}

export interface RuleInfluence {
  ruleId: string;
  ruleName: string;
  confidence?: "high" | "medium" | "low";
}

export interface ContentAnnotation {
  text: string;
  ruleId?: string;
  ruleName?: string;
  type?: "clause" | "phrase" | "requirement";
}

export interface ContractSubSection {
  title: string;
  content?: string;
  subSections?: ContractSubSection[];
  appliedRules?: RuleInfluence[];
  annotations?: ContentAnnotation[];
}

export interface ContractSection {
  title: string;
  hintText: string;
  userContext?: string;
  content?: string;
  subSections?: ContractSubSection[];
  appliedRules?: RuleInfluence[];
  annotations?: ContentAnnotation[];
}

export interface ContractState {
  stage: ContractStage;
  documentType: string;
  documentDescription: string;
  language?: string;
  contractId?: string;
  playbookId?: string;
  parties?: ContractPartyField[];
  otherUserInformation?: ContractPartyField[];
  sections?: ContractSection[];
  regenerate?: { sectionTitle: string; userContext: string };
  jobStatus?: ContractJobStatus;
  jobProgress?: number;
  currentStage?: string;
  jobError?: string;
}

export interface ContractJobStatusResponse {
  contractId: string;
  jobStatus: ContractJobStatus;
  jobProgress: number;
  currentStage?: string;
  jobError?: string;
  state?: ContractState;
  /** While sections are being written: the document so far */
  liveSections?: ContractSection[];
}

/**
 * Submit a contract state to the V2 pipeline.
 * Quick stages (INIT, COMPLETED) return immediately.
 * Long stages (INFO, SECTIONS) return with jobStatus=processing - poll with getContractJobStatus.
 *
 * @param documentId - For web sessions, pass the chatId as the document ID.
 */
export const submitContractState = (
  state: ContractState,
  documentId: string,
): Promise<ApiResponse<ContractState>> => {
  return post<ContractState>("/v2/contract", state, { 'document-id': documentId });
};

/**
 * Poll job status for long-running contract stages.
 */
export const getContractJobStatus = (
  contractId: string,
): Promise<ApiResponse<ContractJobStatusResponse>> => {
  return get<ContractJobStatusResponse>(`/v2/contract/${contractId}/status`, null);
};

/**
 * Get the full contract state.
 */
export const getContract = (
  contractId: string,
): Promise<ApiResponse<ContractState>> => {
  return get<ContractState>(`/v2/contract/${contractId}`, null);
};

const POLL_INTERVAL = 1500;
const MAX_POLL_ATTEMPTS = 800; // ~20 min, matches the backend job timeout
// Polls to wait for the backend to queue SECTIONS after INFO before
// submitting it ourselves
const SECTIONS_HANDOFF_POLLS = 3;

const sleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>(resolve => {
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => {
      clearTimeout(timer);
      resolve();
    }, { once: true });
  });

const failure = (status: number, title: string, detail: string): ApiResponse<ContractState> =>
  ({ success: false, status, errors: [{ title, detail }] } as ApiResponse<ContractState>);

/**
 * Wait until a contract's sections are generated, reporting progress.
 *
 * Covers both long-running stages: after INFO completes the backend queues
 * SECTIONS itself (so generation continues if the user leaves the chat). If
 * that hand-off doesn't happen, SECTIONS is submitted from here.
 *
 * Stops quietly when `signal` aborts (status 499). The backend job keeps
 * running and can be resumed later by calling this again.
 */
export const waitForContractGeneration = async (
  contractId: string,
  documentId: string,
  options: {
    signal?: AbortSignal;
    onProgress?: (progress: number, stage: string, liveSections?: ContractSection[]) => void;
  } = {},
): Promise<ApiResponse<ContractState>> => {
  const { signal, onProgress } = options;
  let handoffPolls = 0;

  for (let i = 0; i < MAX_POLL_ATTEMPTS; i++) {
    if (i > 0) await sleep(POLL_INTERVAL, signal);
    if (signal?.aborted) return failure(499, 'Aborted', 'Stopped waiting for contract');

    const response = await getContractJobStatus(contractId);
    if (signal?.aborted) return failure(499, 'Aborted', 'Stopped waiting for contract');
    if (!response.success || !response.data) {
      return { success: false, status: response.status || 500, errors: response.errors } as ApiResponse<ContractState>;
    }

    const { jobStatus, jobProgress, currentStage, jobError, state, liveSections } = response.data;

    if (jobStatus === ContractJobStatus.FAILED) {
      return failure(500, 'Failed', jobError || 'Contract generation failed');
    }
    if (jobStatus === ContractJobStatus.CANCELLED) {
      return failure(499, 'Cancelled', 'Generation was cancelled');
    }
    if (jobStatus === ContractJobStatus.IDLE) {
      return failure(409, 'Not started', 'Contract details have not been submitted');
    }

    if (jobStatus === ContractJobStatus.COMPLETED && state) {
      if (state.stage >= ContractStage.REGENERATE && state.sections?.length) {
        return { success: true, status: 200, data: state };
      }
      if (state.stage === ContractStage.SECTIONS) {
        // INFO is done; SECTIONS should be queued by the backend shortly
        handoffPolls++;
        if (handoffPolls >= SECTIONS_HANDOFF_POLLS) {
          handoffPolls = 0;
          const submit = await submitContractState(
            { ...state, stage: ContractStage.SECTIONS },
            documentId,
          );
          // 409: the backend queued it in the meantime
          if (!submit.success && submit.status !== 409) {
            return { success: false, status: submit.status || 500, errors: submit.errors } as ApiResponse<ContractState>;
          }
        }
        continue;
      }
      return failure(409, 'Not started', 'Contract details have not been submitted');
    }

    handoffPolls = 0;
    if (onProgress && currentStage) {
      onProgress(jobProgress, currentStage, liveSections);
    }
  }

  return failure(408, 'Timeout', 'Contract generation timed out');
};
